import { useEffect, useState } from 'react'
import { CATEGORY_KEYS, CATEGORY_LABELS, PALETTES, PALETTE_KEYS } from '../../../shared/catalog.js'
import { slugify } from '../../../shared/mentions.js'
import { useCreateSystem, useUpdateSystem } from '../../lib/queries/systems.js'
import { Dialog } from '../ui/Dialog.jsx'
import styles from '../../pages/pages.module.css'
import l from './library.module.css'

const EMPTY = {
  name: '',
  shortName: '',
  vendor: '',
  vendorShort: '',
  category: 'erp',
  description: '',
  palette: 'indigo',
  suggestions: '',
}

function fromSystem(system) {
  if (!system) return EMPTY
  return {
    name: system.name,
    shortName: system.shortName ?? '',
    vendor: system.vendor ?? '',
    vendorShort: system.vendorShort ?? '',
    category: system.category,
    description: system.description ?? '',
    palette: system.palette,
    suggestions: (system.suggestions ?? []).map((s) => s.text).join('\n'),
  }
}

const orNull = (v) => (v.trim() ? v.trim() : null)
/** The tile letter is the first letter of the name. */
const tileLetter = (name) => (name.trim()[0] ?? '?').toUpperCase()

/**
 * Create a system (optimistic card in the list) or edit its details.
 * "Try asking" suggestions are one per line and mention this system.
 */
export function SystemFormDialog({ open, onClose, system, onCreated }) {
  const create = useCreateSystem()
  const update = useUpdateSystem(system?.slug)
  const [form, setForm] = useState(EMPTY)
  const editing = Boolean(system)
  useEffect(() => {
    if (open) setForm(fromSystem(system))
  }, [open, system])

  const set = (key) => (event) => setForm((f) => ({ ...f, [key]: event.target.value }))
  const slug = slugify(form.name)
  const valid = form.name.trim().length >= 2 && slug

  const submit = () => {
    const base = {
      name: form.name.trim(),
      shortName: form.shortName.trim() || form.name.trim(),
      letter: tileLetter(form.name),
      vendor: orNull(form.vendor),
      vendorShort: orNull(form.vendorShort),
      category: form.category,
      tagline: null,
      description: orNull(form.description),
      palette: form.palette,
    }
    if (editing) {
      const suggestions = form.suggestions
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => line.length >= 3)
        .slice(0, 6)
        .map((text) => {
          const existing = system.suggestions?.find((s) => s.text === text)
          return existing ?? { text, systems: [system.slug] }
        })
      update.mutate({ ...base, suggestions })
    } else {
      create.mutate({ ...base, slug, categoryLabel: CATEGORY_LABELS[form.category] })
      onCreated?.(slug)
    }
    onClose()
  }

  const fields = (
    <>
      <div className={styles.formGrid}>
        <label className="field">
          <span>Name</span>
          <input value={form.name} onChange={set('name')} placeholder="NetSuite" autoFocus maxLength={60} />
          {!editing && slug ? <small>Slug: {slug}</small> : null}
        </label>
        <label className="field">
          <span>Short name (filters)</span>
          <input value={form.shortName} onChange={set('shortName')} placeholder="NetSuite" maxLength={16} />
        </label>
        <label className="field">
          <span>Category</span>
          <select value={form.category} onChange={set('category')}>
            {CATEGORY_KEYS.map((key) => (
              <option key={key} value={key}>
                {CATEGORY_LABELS[key]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Vendor</span>
          <input value={form.vendor} onChange={set('vendor')} placeholder="Oracle NetSuite" maxLength={80} />
        </label>
      </div>
      <label className="field">
        <span>Description</span>
        <textarea value={form.description} onChange={set('description')} maxLength={600} />
      </label>
      {editing ? (
        <label className="field">
          <span>“Try asking” suggestions (one per line)</span>
          <textarea value={form.suggestions} onChange={set('suggestions')} />
        </label>
      ) : null}
      <div className="field">
        <span>Colour</span>
        <div className={l.swatches} role="radiogroup" aria-label="Colour">
          {PALETTE_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              role="radio"
              aria-checked={form.palette === key}
              aria-label={key}
              className={`${l.swatch} ${form.palette === key ? l.swatchOn : ''}`}
              style={{ background: PALETTES[key].tile }}
              onClick={() => setForm((f) => ({ ...f, palette: key }))}
            >
              {form.name.trim() ? tileLetter(form.name) : ''}
            </button>
          ))}
        </div>
      </div>
    </>
  )

  return (
    <Dialog
      open={open}
      onClose={onClose}
      width={600}
      title={editing ? `Edit ${system.name}` : 'Add a system'}
      description={editing ? null : 'Add the system first, then upload its skill files (SKILL.md, references, assets).'}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-dark" disabled={!valid} onClick={submit}>
            {editing ? 'Save' : 'Add system'}
          </button>
        </>
      }
    >
      {fields}
    </Dialog>
  )
}
